import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { errorMessage } from '../lib/api';
import { Spinner } from '../components/Spinner';
import { RotateCw } from 'lucide-react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { followSessionSlot } from '../lib/accountSlot';

export function LoginPage() {
  useDocumentTitle('Sign in');
  const { login, isAuthenticated, initializing } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const state = location.state as { from?: string; passwordReset?: boolean } | null;
  const from = state?.from ?? '/';
  const adding = new URLSearchParams(location.search).get('add') === '1';
  const passwordReset = state?.passwordReset ?? false;

  if (!initializing && isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await login({ email: email.trim(), password });
      if (res.requiresTotp && res.mfaToken) {
        // Real history navigation (not an in-place swap) so password
        // managers re-scan the page and offer the TOTP code.
        navigate('/login/mfa', { state: { mfaToken: res.mfaToken, from } });
      } else {
        if (followSessionSlot(res, from)) return;
        navigate(from, { replace: true });
      }
    } catch (err) {
      setError(errorMessage(err, 'Invalid email or password'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center px-4">
      <div className="w-full max-w-sm animate-fade-in">
        <div className="mb-8 flex flex-col items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-ink text-white">
            <RotateCw className="h-6 w-6" aria-hidden="true" />
          </span>
          <div className="text-center">
            <h1 className="text-lg font-semibold tracking-tight text-ink">
              aiai·loop
            </h1>
            <p className="mt-1 text-sm text-ink-muted">
              Sign in to orchestrate your agents
            </p>
          </div>
        </div>

        {passwordReset && (
          <div className="mb-4 rounded-[5px] border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-800">
            Your password has been reset. Sign in with your new password.
          </div>
        )}

        {adding && <p className="mb-4 text-center text-sm text-ink-muted">Add another account</p>}

        <form onSubmit={onSubmit} className="card space-y-4 p-6">
          <div>
            <label className="label" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="username"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoFocus
            />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="label" htmlFor="password">
                Password
              </label>
              <Link
                to="/forgot-password"
                className="text-xs text-ink-muted hover:text-ink"
              >
                Forgot password?
              </Link>
            </div>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
          </div>

          {error && (
            <div className="rounded-[5px] border border-bad/30 bg-bad-soft px-3 py-2 text-sm text-bad">
              {error}
            </div>
          )}

          <button type="submit" className="btn-primary w-full" disabled={submitting}>
            {submitting && <Spinner />}
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        {adding && (
          <p className="mt-4 text-center text-sm">
            <a href="/accounts" className="text-ink-muted hover:text-ink">
              Cancel
            </a>
          </p>
        )}
      </div>
    </div>
  );
}
